import {useCallback, useEffect, useMemo, useState} from 'react';

import logError from 'src/utils/errors/logError';

import {firestoreDB} from '@/services/firebase/initializeFirebase';
import {isProduction} from '@/utils/environment';

import {
  ApplePayFlagData,
  ApplePayFlagState,
  resolveApplePayFlagState,
} from './applePayFlag';
import {buildRemoteFlagsMap} from './remoteFeatureFlagsMap';

const featureFlagsCollection = 'featureFlags';
const APPLE_PAY_FLAG = 'APPLE_PAY_API_ENABLED';

const getAppEnv = () => {
  if (isProduction) return 'production';
  return 'development';
};

interface IFeatureFlag {
  featureName: string;
  parentFeature?: string | null;
}

let cachedFlags: Record<string, IFeatureFlag> | null = null;
let cachedApplePayData: ApplePayFlagData | null = null;
let flagsRequest: Promise<Record<string, IFeatureFlag>> | null = null;

const loadRemoteFeatureFlags = async (
  forceRefetch = false,
): Promise<Record<string, IFeatureFlag>> => {
  if (!forceRefetch && cachedFlags) {
    return cachedFlags;
  }

  if (!forceRefetch && flagsRequest) {
    return flagsRequest;
  }

  const env = getAppEnv();

  flagsRequest = firestoreDB
    .collection(featureFlagsCollection)
    .get()
    .then(snapshot => {
      const nextFlags: Record<string, IFeatureFlag> = {};
      let nextApplePayData: ApplePayFlagData | null = null;

      snapshot.docs.forEach(doc => {
        const data = doc.data();

        const isEnabledForEnv =
          data?.environments?.[env] === true &&
          data?.allowForEveryone?.[env] === true;

        if (data?.featureName && isEnabledForEnv) {
          nextFlags[data.featureName] = {
            featureName: data.featureName,
            parentFeature: data.parentFeature ?? null,
          };
        }

        // Apple Pay needs per-link (testers) scoping, so we keep its raw data
        // regardless of allowForEveryone — the generic map can't express it.
        if (data?.featureName === APPLE_PAY_FLAG) {
          nextApplePayData = {
            environments: data?.environments ?? null,
            allowForEveryone: data?.allowForEveryone ?? null,
            testers: Array.isArray(data?.testers) ? data.testers : null,
          };
        }
      });

      cachedFlags = nextFlags;
      cachedApplePayData = nextApplePayData;
      return nextFlags;
    })
    .finally(() => {
      flagsRequest = null;
    });

  return flagsRequest;
};

export const useRemoteFeatureFlags = () => {
  const [flags, setFlags] = useState<Record<string, IFeatureFlag>>(
    () => cachedFlags ?? {},
  );
  const [applePayData, setApplePayData] = useState<ApplePayFlagData | null>(
    () => cachedApplePayData,
  );
  const [isLoading, setLoading] = useState(() => cachedFlags === null);
  const [hasError, setError] = useState(false);

  const fetchFlags = useCallback(async (forceRefetch = false) => {
    setLoading(true);
    try {
      setError(false);
      const nextFlags = await loadRemoteFeatureFlags(forceRefetch);
      setFlags(nextFlags);
      setApplePayData(cachedApplePayData);
    } catch (error) {
      setError(true);
      logError(error, {
        fileName: 'useRemoteFeatureFlags.ts',
        functionName: 'fetchFlags',
        customTitle: 'Failed to fetch feature flags',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isActive = true;

    const syncFlags = async () => {
      setLoading(true);
      try {
        setError(false);
        const nextFlags = await loadRemoteFeatureFlags();

        if (!isActive) return;

        setFlags(nextFlags);
        setApplePayData(cachedApplePayData);
      } catch (error) {
        if (!isActive) return;

        setError(true);
        logError(error, {
          fileName: 'useRemoteFeatureFlags.ts',
          functionName: 'fetchFlags',
          customTitle: 'Failed to fetch feature flags',
        });
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    syncFlags();

    return () => {
      isActive = false;
    };
  }, [fetchFlags]);

  const isFeatureEnabled = useCallback(
    (key: string, visited = new Set<string>()): boolean => {
      if (visited.has(key)) return false;
      visited.add(key);

      const flag = flags[key];
      if (!flag) return false;

      if (flag.parentFeature) {
        return isFeatureEnabled(flag.parentFeature, visited);
      }

      return true;
    },
    [flags],
  );

  const getApplePayFlagState = useCallback(
    (paymentLinkId?: string): ApplePayFlagState =>
      resolveApplePayFlagState(applePayData, getAppEnv(), paymentLinkId),
    [applePayData],
  );

  const flagsMap = useMemo(
    () => buildRemoteFlagsMap(isFeatureEnabled),
    [isFeatureEnabled],
  );

  return {
    flags,
    isLoading,
    hasError,
    isFeatureEnabled,
    flagsMap,
    getApplePayFlagState,
    refetch: () => fetchFlags(true),
  };
};
